import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import NearMeIcon from "@mui/icons-material/NearMe";
import ReplayIcon from "@mui/icons-material/Replay";
import VerifiedIcon from "@mui/icons-material/Verified";

import useAssessmentStore from "components/Assessment/assessmentStore";
import { QuestionStates } from "components/Assessment/constants";

import CountDown from "./CountDown";
import VideoPreview from "./VideoPreview";
import SpeakingIndicator from "./SpeakingIndicator";

/**
 * The interview card (mobile): camera banner, question, per-question timer and
 * the record controls.
 *
 * The button set replicates desktop `Assessment/Controls` exactly:
 *   - Record Answer  — while WAITING/SUBMITTED and the question isn't submitted
 *   - Save Answer    — while RECORDING
 *   - Retake         — on error, or for practice templates after submitting
 *   - Submitted      — assignment templates after submitting (disabled)
 */
export default function QuestionCard() {
  const questions = useAssessmentStore((s) => s.questions);
  const currentQuestion = useAssessmentStore((s) => s.currentQuestion);
  const questionState = useAssessmentStore((s) => s.questionState);
  const questionsError = useAssessmentStore((s) => s.questionsError);
  const templateType = useAssessmentStore((s) => s.template?.type);
  const isInterviewerSpeaking = useAssessmentStore(
    (s) => s.isInterviewerSpeaking
  );
  const start = useAssessmentStore((s) => s.start);
  const submit = useAssessmentStore((s) => s.submit);

  const question = React.useMemo(
    () => questions[currentQuestion] || {},
    [questions, currentQuestion]
  );

  React.useEffect(() => {
    if (question) question.visited = true;
  }, [question]);

  const isRecording = questionState === QuestionStates.RECORDING;
  const isIdle = ["WAITING", "SUBMITTED"].includes(questionState);
  const hasError = questionsError?.[currentQuestion];
  // Practice lets you retake an answer; assignment locks it once submitted.
  const isRetakeable = templateType === "practice";

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "shades.clr-white-900",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "neutral.clr-100",
      }}
    >
      <VideoPreview />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
          >
            <Typography variant="body01-bold" color="primary.clr-300">
              Question {currentQuestion + 1} of {questions.length}
            </Typography>
            {question?.type && (
              <Chip
                label={`${question.type} question`}
                size="small"
                sx={{
                  height: 24,
                  bgcolor: "primary.clr-50",
                  color: "primary.clr-300",
                  border: "1px solid",
                  borderColor: "primary.clr-100",
                  textTransform: "capitalize",
                  "& .MuiChip-label": { px: 2, fontSize: 11, fontWeight: 600 },
                }}
              />
            )}
          </Box>

          {/* Live mic animation while recording — desktop MicTestAnimation. */}
          {isRecording && <SpeakingIndicator />}
        </Box>

        {/* Only this region scrolls when the question is long. */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Typography
            variant="h6-bold"
            color="shades.clr-black-900"
            sx={{ lineHeight: 1.45 }}
            // Questions are authored as HTML, same as desktop's QuestionSection.
            dangerouslySetInnerHTML={{ __html: question?.question || "" }}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            mt: 1,
          }}
        >
          <CountDown state={questionState} question={question} />

          <Box sx={{ display: "flex", gap: 2 }}>
            {((isIdle && !question.submitted) ||
              questionState === QuestionStates.SHOWING_TUTORIAL) && (
              <Button
                variant="contained"
                onClick={start}
                disabled={questionState === "SUBMITTED" || isInterviewerSpeaking}
                startIcon={<MicIcon sx={{ fontSize: 18 }} />}
              >
                Record Answer
              </Button>
            )}

            {isRecording && (
              <Button
                variant="contained"
                onClick={submit}
                startIcon={<NearMeIcon sx={{ fontSize: 18 }} />}
              >
                Save Answer
              </Button>
            )}

            {(hasError || (isIdle && isRetakeable && question.submitted)) && (
              <Button
                variant="outlined"
                onClick={start}
                disabled={questionState === "SUBMITTED"}
                startIcon={<ReplayIcon sx={{ fontSize: 18 }} />}
              >
                Retake
              </Button>
            )}

            {!hasError && isIdle && question.submitted && !isRetakeable && (
              <Button
                variant="contained"
                disabled
                startIcon={<VerifiedIcon sx={{ fontSize: 18 }} />}
              >
                Submitted
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
